<template>
    <div v-if="loading" class="flex min-h-80 items-center justify-center"><Loader /></div>
    <div v-else class="p-4 ds-exchange-panel-content">
        <div class="ds-exchange-panel-title pb-5">Создание запроса</div>
        <div class="ds-exchange-panel-section border-grayxlight border-b mb-2">Основные данные</div>

        <div class="mb-3">
            <div class="mb-3 ">
                <div class="ds-exchange-field-label">В магазин</div>
                <dropdown v-if="department" v-model="department" :options="departmentStore.myDepartments" direction="down" :search="true" @onsearch="onMyDepSearch">
                    {{ scheduleStore.cutStr(department.name, 54) }}
                </dropdown>
            </div>

            <div class="mb-3 ">
                <div class="ds-exchange-field-label">Из магазина</div>
                <dropdown v-model="requestedDepartment" :options="departments" direction="down" :search="true" @onsearch="onDepSearch">
                    {{ scheduleStore.cutStr(requestedDepartment.name, 54) }}
                </dropdown>
            </div>

            <div class="mb-3 ">
                <div class="ds-exchange-field-label">Должность</div>
                <dropdown v-model="position" :options="positions" class="text-xs">
                    {{ scheduleStore.cutStr(position.name, 54) }}
                </dropdown>
            </div>

            <div class="flex">
                <div class="mb-2 mr-5">
                    <div class="ds-exchange-field-label">Дата</div>
                    <AppDateInput v-model="date" />
                </div>   

                <div class="mr-5 mb-3">
                    <div class="ds-exchange-field-label">Время начала</div>
                    <time-picker v-model="start" />
                </div>

                <div class="mr-5 mb-3">
                    <div class="ds-exchange-field-label">Время окончания</div>
                    <time-picker v-model="end" />
                </div>
            </div>

            <div class="mb-3 ">
                <div class="ds-exchange-field-label">Сотрудник</div>
                <dropdown v-model="employee" :options="employees" class="text-xs">
                    {{ scheduleStore.cutStr(employee.name, 24) }}
                </dropdown>
            </div>

            <base-input v-model="price" max-length="5" class="mb-2" @input="onInputDigits">Ставка в час</base-input>
        </div>

        <div class="ds-exchange-field-label">Комментарий</div>
        <textarea v-model="comment" class="ds-exchange-textarea w-full mb-8" maxlength="100"></textarea>

        <div class="pt-3 text-right">
            <btn @click="save" :disabled="isDisabled" :class="{ 'opacity-50': isDisabled }">Сохранить</btn>
            <btn class="ml-3" type="secondary" @click="cancel">Отмена</btn>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch, computed } from 'vue'
import { Option } from '@/types'
import dayjs from 'dayjs'
import utc from "dayjs/plugin/utc"
import Loader from '@/ui/Loader.vue'
import AppDateInput from '@/ui/AppDateInput.vue'
import TimePicker from '@/components/TimePicker.vue'
import debounce from 'lodash.debounce'
import { useScheduleStore } from '@/stores/scheduleStore'
import { useDepartmentStore } from '@/stores/departmentStore'
import { useEmploymentStore } from '@/stores/employmentStore'
import { useExchangeStore } from '@/stores/exchangeStore'

const departmentStore = useDepartmentStore()
const scheduleStore = useScheduleStore()
const employmentStore = useEmploymentStore()
const exchangeStore = useExchangeStore()
const emit = defineEmits(['close'])
dayjs.extend(utc)

const positions = computed(() => [{id: "", name: 'Выберите должность'}, ...scheduleStore.positions])
const employees = computed(() => employmentStore.employees.map(item => {return {id: item.id, name: `${item.last_name} ${item.first_name}`}}))
const employee = ref<Option>({id: '', name: 'Выберите сотрудника'})

const loading = ref(false)
const date = ref(dayjs())
const start = ref('09:00')
const end = ref('18:00')
const today = new Date()
const yesterday = new Date(today)
yesterday.setDate(yesterday.getDate() - 1)
const comment = ref('')
const isDisabled = ref(true)
const position = ref<Option>(positions.value[0])
const department = ref<Option>(departmentStore.myDepartments[0])
const requestedDepartment = ref<Option>({id: "", name: 'Выберите магазин'})
const price = ref('100')
const skills = ref<any[]>([])
const timeoutId = ref()

const departments = computed(() => departmentStore.departments.filter(dep => dep.id !== department.value.id))

const validation = () => {
    isDisabled.value = !(price.value.length > 1 && dayjs(yesterday).isBefore(dayjs(date.value)) && (start.value < end.value) && position.value.id && employee.value.id)
}

const onInputDigits = () => {
    price.value = price.value.replace(/\D/g, '');
}

const cancel = () => {
    emit('close')
}

const onMyDepSearch = debounce((text: string) => {
    const data = { search: text }
    departmentStore.fetchMyDepartments(data)
}, 500) 

const onDepSearch = debounce((text: string) => {
    const data = { search: text }
    departmentStore.fetchDepartments(data)
}, 500) 

const getEmployeesAvailable = async () => {
    employmentStore.employees = []
    const formatDate = dayjs(date.value).format('YYYY-MM-DD')
    const data = {
        start: `${formatDate}T${start.value}:00.000Z`,
        end: `${formatDate}T${end.value}:00.000Z`,
        id: requestedDepartment.value.id
    }
    employee.value = {id: '', name: 'Выберите сотрудника'}
    if(date.value && start.value && end.value && requestedDepartment.value.id) 
    await employmentStore.fetchAvailableEmployees(data)
}

const save = async () => {        
    isDisabled.value = true
    const formatDate = dayjs(date.value).format('YYYY-MM-DD')
    let data = {
        date: formatDate,
        dttm_work_start_at: `${formatDate}T${start.value}:00.000Z`,
        dttm_work_end_at: `${formatDate}T${end.value}:00.000Z`,
        department: department.value.id,
        position: position.value.id,
        comment: comment.value,
        salary_hour: price.value,
        requested_department: requestedDepartment.value.id,
        requested_user: employee.value.id
    }
    await exchangeStore.addVacancy(data)
    isDisabled.value = false
}

onMounted(async () => {
    validation()
})

watch([date, price, start, end, position, employee], () => { validation() })

watch(position, () => {
    skills.value = scheduleStore.positions.find(item => item.id === position.value.id)?.skills || []
})

watch([date, requestedDepartment], async () => { 
    await getEmployeesAvailable()
 })

 watch([start, end], async () => { 
    clearTimeout(timeoutId.value)
    timeoutId.value = setTimeout(getEmployeesAvailable, 1000)
 })
    
</script>
